import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Home, FileText, PieChart, Settings, LogOut, ChevronLeft, ChevronRight, Sun, Moon, Target, LayoutDashboard, Wrench, Megaphone } from 'lucide-react'
import { useUIStore } from '@store/uiStore'
import { useAuthStore } from '@store/authStore'
import { clsx } from 'clsx'
import { ReleaseNotesModal } from './ReleaseNotesModal'

const homeItem = { path: '/home', label: 'Home', icon: Home }

const navItems = [
  { path: '/quote', label: 'Quote', icon: FileText },
  { path: '/roi', label: 'ROI Calculator', icon: PieChart },
  { path: '/dashboard/presales/tools', label: 'Tools', icon: Wrench },
  { path: '/dashboard', label: 'Dashboards', icon: LayoutDashboard },
  { path: '/target-accounts', label: 'Target Accounts', icon: Target },
]

const toolItems = [
  { path: '/settings', label: 'Settings', icon: Settings },
]

export function Sidebar() {
  const [showReleaseNotes, setShowReleaseNotes] = useState(false)
  const { sidebarOpen, sidebarCollapsed, setSidebarCollapsed, darkMode, toggleDarkMode } = useUIStore()
  const { user, signOut } = useAuthStore()
  const location = useLocation()

  const handleNavClick = () => {
    // Collapse sidebar when a nav item is clicked
    setSidebarCollapsed(true)
  }

  const toggleCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  return (
    <aside
      className={clsx(
        'fixed top-0 left-0 h-full z-30',
        'transform transition-all duration-300',
        'hidden md:flex md:flex-col',
        sidebarCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRight: '1px solid var(--border-color)'
      }}
    >
      {/* Logo with Collapse Toggle */}
      <div className="px-4 py-4 border-b border-[var(--border-color)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">F1</span>
            </div>
            {!sidebarCollapsed && (
              <span className="font-semibold text-[var(--text-primary)] whitespace-nowrap">DealHub</span>
            )}
          </div>
          {!sidebarCollapsed && (
            <button
              onClick={toggleCollapse}
              className="p-1.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-md transition-colors"
              title="Collapse sidebar"
            >
              <ChevronLeft size={18} />
            </button>
          )}
          {sidebarCollapsed && (
            <button
              onClick={toggleCollapse}
              className="p-1.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-md transition-colors"
              title="Expand sidebar"
            >
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 overflow-y-auto">
        {/* Home */}
        <div className="mb-2">
          <NavLink
            to={homeItem.path}
            onClick={handleNavClick}
            title={sidebarCollapsed ? homeItem.label : undefined}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                sidebarCollapsed && 'justify-center',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
              )
            }
          >
            <homeItem.icon size={18} className="flex-shrink-0" />
            {!sidebarCollapsed && homeItem.label}
          </NavLink>
        </div>

        {/* Separator line */}
        <div className="mx-3 mb-3 border-t border-[var(--border-color)]" />

        {/* Navigation items */}
        <div className="mb-4">
          {!sidebarCollapsed && (
            <p className="px-3 mb-2 text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
              Navigation
            </p>
          )}
          {navItems.map((item) => {
            // Tools is a sub-path of /dashboard — check exact prefix to avoid double-highlight
            const isItemActive = item.path === '/dashboard'
              ? location.pathname.startsWith('/dashboard') && !location.pathname.startsWith('/dashboard/presales/tools')
              : location.pathname.startsWith(item.path)
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                title={sidebarCollapsed ? item.label : undefined}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                  sidebarCollapsed && 'justify-center',
                  isItemActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
                )}
              >
                <item.icon size={18} className="flex-shrink-0" />
                {!sidebarCollapsed && item.label}
              </NavLink>
            )
          })}
        </div>

        {/* Separator line before tools */}
        <div className="mx-3 mb-3 border-t border-[var(--border-color)]" />

        {/* Tools */}
        <div>
          {toolItems.map((item) => {
            // Use startsWith matching so /settings/hours still highlights the Settings link
            const isToolActive = location.pathname.startsWith(item.path)
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                title={sidebarCollapsed ? item.label : undefined}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                  sidebarCollapsed && 'justify-center',
                  isToolActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
                )}
              >
                <item.icon size={18} className="flex-shrink-0" />
                {!sidebarCollapsed && item.label}
              </NavLink>
            )
          })}
        </div>
      </nav>

      {/* What's New */}
      <div className="px-2 py-1 border-t border-[var(--border-color)]">
        <button
          onClick={() => setShowReleaseNotes(true)}
          className={clsx(
            'w-full flex items-center gap-3 px-3 py-2 rounded-md text-xs transition-colors',
            sidebarCollapsed && 'justify-center',
            'text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-secondary)]'
          )}
          title="What's New"
        >
          <Megaphone size={14} className="flex-shrink-0" />
          {!sidebarCollapsed && <span>What's New</span>}
        </button>
      </div>

      <ReleaseNotesModal open={showReleaseNotes} onClose={() => setShowReleaseNotes(false)} />

      {/* Theme Toggle */}
      <div className="px-2 py-2 border-t border-[var(--border-color)]">
        <button
          onClick={toggleDarkMode}
          className={clsx(
            'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
            sidebarCollapsed && 'justify-center',
            'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
          )}
          title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {darkMode ? (
            <>
              <Sun size={18} className="flex-shrink-0" />
              {!sidebarCollapsed && <span>Light Mode</span>}
            </>
          ) : (
            <>
              <Moon size={18} className="flex-shrink-0" />
              {!sidebarCollapsed && <span>Dark Mode</span>}
            </>
          )}
        </button>
      </div>

      {/* User Profile */}
      <div className="px-2 py-4 border-t border-[var(--border-color)]">
        <div className={clsx(
          'flex items-center gap-3 px-3 py-2',
          sidebarCollapsed && 'justify-center'
        )}>
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-8 h-8 rounded-full flex-shrink-0"
              title={sidebarCollapsed ? user.name : undefined}
            />
          ) : (
            <div
              className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0"
              title={sidebarCollapsed ? user?.name || 'User' : undefined}
            >
              <span className="text-primary text-sm font-medium">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
          )}
          {!sidebarCollapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-[var(--text-muted)] truncate">{user?.email}</p>
              </div>
              <button
                onClick={signOut}
                className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-md transition-colors"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            </>
          )}
        </div>
        {sidebarCollapsed && (
          <button
            onClick={signOut}
            className="w-full flex items-center justify-center px-3 py-2 mt-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-md transition-colors"
            title="Sign out"
          >
            <LogOut size={16} />
          </button>
        )}
      </div>
    </aside>
  )
}
